import './ActiveSubstations.css';
import React, { useEffect, useMemo, useState } from "react";
import { getActiveSubstations } from './api';

const monthOptions = [
    { value: 'januar', label: 'Januar' },
    { value: 'februar', label: 'Februar' },
    { value: 'mart', label: 'Mart' },
    { value: 'april', label: 'April' },
    { value: 'maj', label: 'Maj' },
    { value: 'juni', label: 'Juni' },
    { value: 'juli', label: 'Juli' },
    { value: 'avgust', label: 'Avgust' },
    { value: 'septembar', label: 'Septembar' },
    { value: 'oktobar', label: 'Oktobar' },
    { value: 'novembar', label: 'Novembar' },
    { value: 'decembar', label: 'Decembar' },
];

const getCurrentMonthName = () => {
    const monthIndex = new Date().getMonth();
    return monthOptions[monthIndex]?.value || 'januar';
};

const formatNumber = (value) => {
    const num = Number(value || 0);
    return new Intl.NumberFormat('sr-RS', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(num);
};

const ActiveSubstationsModalContent = ({ isOpen, vodId, pripadnostVodu }) => {
    const [substations, setSubstations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [month, setMonth] = useState(getCurrentMonthName());
    const [year, setYear] = useState(new Date().getFullYear());
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const currentYear = new Date().getFullYear();
    const years = useMemo(
        () => Array.from({ length: 11 }, (_, i) => currentYear - i),
        [currentYear]
    );

    useEffect(() => {
        if (!isOpen) {
            setError('');
            setIsLoading(false);
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleFetch = async () => {
        if (!pripadnostVodu) {
            setError('Nije izabran nijedan vod.');
            setSubstations([]);
            setHasSearched(true);
            return;
        }

        setIsLoading(true);
        setError('');
        setHasSearched(true);

        try {
            const data = await getActiveSubstations({
                pripadnostVodu,
                month,
                year,
            });

            setSubstations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Greška pri dohvaćanju aktivnih trafostanica:', err);
            setSubstations([]);
            setError('Greška pri dohvaćanju podataka.');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredSubstations = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();

        if (!term) return substations;

        return substations.filter((substation) =>
            (substation?.naziv || '').toLowerCase().includes(term)
        );
    }, [substations, searchTerm]);

    return (
        <div className="active-substations-div-modal">
            <div className="active-substations-toolbar">
                <div className="active-substations-toolbar-left">
                    <div className="active-substations-field">
                        <label className="active-substations-field-label">Mjesec</label>
                        <select
                            className="select-month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                        >
                            {monthOptions.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="active-substations-field">
                        <label className="active-substations-field-label">Godina</label>
                        <select
                            className="select-year"
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {years.map((yearValue) => (
                                <option key={yearValue} value={yearValue}>
                                    {yearValue}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        className="active-substations-fetch-btn"
                        onClick={handleFetch}
                        disabled={isLoading || !pripadnostVodu}
                    >
                        {isLoading ? 'Učitavanje...' : 'Prikaži podatke'}
                    </button>
                </div>

                <div className="active-substations-toolbar-right">
                    <div className="active-substations-toolbar-meta">
                        {pripadnostVodu ? (
                            <>
                                <span className="active-substations-meta-label">Vod:</span>
                                <span className="active-substations-meta-value">
                                    {vodId || pripadnostVodu}
                                </span>
                            </>
                        ) : (
                            <span className="active-substations-meta-empty">
                                Nije izabran nijedan vod
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {hasSearched && substations.length > 0 && !isLoading && !error && (
                <div className="active-substations-searchbar-wrap">
                    <div className="active-substations-searchbar">
                        <label className="active-substations-searchbar-label">
                            Pretraga trafostanice
                        </label>
                        <input
                            type="text"
                            className="active-substations-search-input"
                            placeholder="Unesi naziv trafostanice..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="active-substations-search-count">
                        Prikazano: <strong>{filteredSubstations.length}</strong>
                        {' / '}
                        <strong>{substations.length}</strong>
                    </div>
                </div>
            )}

            {isLoading ? (
                <div className="active-substations-loading-wrap">
                    <div className="loading-spinner-active-substations"></div>
                </div>
            ) : error ? (
                <div className="active-substations-state-message is-error">
                    {error}
                </div>
            ) : !hasSearched ? (
                <div className="active-substations-state-message">
                    Odaberi period i klikni na <strong>Prikaži podatke</strong>.
                </div>
            ) : filteredSubstations && filteredSubstations.length > 0 ? (
                <div className="active-substations-results">
                    {filteredSubstations.map((substation, index) => (
                        <div className="active-substation-item" key={`${substation.naziv}-${index}`}>
                            <div className="active-substation-item-header">
                                <div className="active-substation-title-wrap">
                                    <div className="active-substation-kicker">Trafostanica</div>
                                    <h4 className="active-substation-title">
                                        {substation.naziv || 'Bez naziva'}
                                    </h4>
                                </div>
                            </div>

                            <div className="active-substation-metrics">
                                <div className="active-substation-metric">
                                    <span className="active-substation-metric-label">
                                        Potrošnja mjesečna
                                    </span>
                                    <span className="active-substation-metric-value">
                                        {formatNumber(substation.potrosnja_mjesecna)}
                                    </span>
                                </div>

                                <div className="active-substation-metric">
                                    <span className="active-substation-metric-label">
                                        Sumarno AMT
                                    </span>
                                    <span className="active-substation-metric-value">
                                        {formatNumber(substation.sum_amt)}
                                    </span>
                                </div>

                                <div className="active-substation-metric">
                                    <span className="active-substation-metric-label">
                                        Sumarno AVT
                                    </span>
                                    <span className="active-substation-metric-value">
                                        {formatNumber(substation.sum_avt)}
                                    </span>
                                </div>

                                <div className="active-substation-metric">
                                    <span className="active-substation-metric-label">
                                        Obračunska snaga
                                    </span>
                                    <span className="active-substation-metric-value">
                                        {formatNumber(substation.sum_obracunska_snaga)}
                                    </span>
                                </div>

                                <div className="active-substation-metric">
                                    <span className="active-substation-metric-label">
                                        Prekovremena RVT
                                    </span>
                                    <span className="active-substation-metric-value">
                                        {formatNumber(substation.sum_rvt)}
                                    </span>
                                </div>
                            </div>

                            <div className="active-substation-type-block">
                                <div className="active-substation-type-label">Tip brojila</div>
                                <div className="active-substation-type-value">
                                    {substation.tip_brojila || 'Nema podatka'}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : hasSearched && substations.length > 0 ? (
                <div className="active-substations-state-message">
                    Nema trafostanica koje odgovaraju unesenoj pretrazi.
                </div>
            ) : (
                <div className="active-substations-state-message">
                    Nema podataka za izabrani period.
                </div>
            )}
        </div>
    );
};

export default ActiveSubstationsModalContent;